import { mapGetters } from "vuex";

import topbarWidget from "@/components/features/widgets/topbar-widget/index.vue";
import footerBannerWidget from "@/components/features/widgets/footer-banner-widget/index.vue";
import footerPromoWidget from "@/components/features/widgets/footer-promo-widget/index.vue";
import genericWidget from "@/components/features/widgets/generic-widget/index.vue";
import customHtmlWidget from "@/components/features/widgets/custom-html-widget/index.vue";

const DISMISS_KEY_PREFIX = "gs_widget_dismissed_";
const DAY_MS = 86400000;

function dismissKey(id) {
	return `${DISMISS_KEY_PREFIX}${id}`;
}

// Читаємо dismiss-стан за контрактом C (session/day/forever) з sessionStorage/localStorage.
function isPersistedDismissed(id) {
	const key = dismissKey(id);

	try {
		if (sessionStorage.getItem(key) === "1") {
			return true;
		}

		const stored = localStorage.getItem(key);

		if (stored === null) {
			return false;
		}

		if (stored === "1") {
			return true;
		}

		const showAgainAt = Number(stored);

		if (!Number.isNaN(showAgainAt)) {
			return Date.now() < showAgainAt;
		}

		return false;
	} catch (e) {
		return false;
	}
}

function persistDismiss(id, scope) {
	// 'view' — legacy-поведінка старого банера: нічого не персистимо,
	// закриття живе лише в памʼяті хоста (наступний вхід на сторінку = показ).
	if (scope === "view") {
		return;
	}

	const key = dismissKey(id);

	try {
		if (scope === "day") {
			localStorage.setItem(key, String(Date.now() + DAY_MS));
		} else if (scope === "forever") {
			localStorage.setItem(key, "1");
		} else {
			sessionStorage.setItem(key, "1");
		}
	} catch (e) {
		// storage може бути недоступним (private mode) — віджети не критичні
	}
}

export default {
	name	: "widget-host",
	components: {
		"topbar-widget": topbarWidget,
		"footer-banner-widget": footerBannerWidget,
		"footer-promo-widget": footerPromoWidget,
		"generic-widget": genericWidget,
		"custom-html-widget": customHtmlWidget,
	},
	props	: {
		placement: {
			type		: String,
			default	: "global",
		},
		current: {
			type		: Object,
			default	: null,
		},
		isLesson: {
			type		: Boolean,
			default	: false,
		},
	},
	data() {
		return {
			dismissedIds : new Set(),
		};
	},
	computed: {
		...mapGetters({
			setting : "setting/setting",
			isAuthenticated : "auth/isAuthenticated",
		}),
		widgetsSource() {
			const setting = this.setting || {};

			return setting.widgetsV2 || setting.widgets_v2 || [];
		},
		visibleWidgets() {
			return this.widgetsSource.filter(w => this.isVisible(w));
		},
	},
	methods: {
		isVisible(widget) {
			// Віджети рендеримо ТІЛЬКИ авторизованим користувачам. На публічних
			// (гостьових) сторінках — як відкрита без логіна сторінка курсу — не
			// показуємо жоден віджет для будь-якого placement (global/course).
			if (!this.isAuthenticated) {
				return false;
			}

			if (!widget) {
				return false;
			}

			// custom_html з closable === true рендериться через host (з хрестиком).
			// custom_html без closable — легасі-шлях (edu-backend/App.vue innerHTML),
			// його НЕ рендеримо тут. Порожній content — теж пропускаємо, інакше
			// на сторінці висів би самотній хрестик без плашки.
			if (widget.type === "custom_html") {
				const closable = widget.variables && widget.variables.closable === true;
				const targeted = Array.isArray(widget.courseIds) && widget.courseIds.length > 0;
				const hasContent = typeof widget.content === "string" && widget.content.trim() !== "";

				// The host renders any custom_html that uses a v2-only
				// capability: a dismiss control (closable) OR course targeting
				// (the legacy channel is app-wide and can't honour courses).
				// Plain global records stay on the legacy inline path. The
				// backend mirrors this exact condition when building the
				// legacy `widgets` key, so a record is never rendered twice.
				if ((!closable && !targeted) || !hasContent) {
					return false;
				}
			}

			// Невідомий тип (немає шаблона) — пропускаємо, щоб не рендерити порожній вузол.
			if (this.templateFor(widget.type) === null) {
				return false;
			}

			// footer_banner без картинок — недоукомплектований запис (картинки
			// додаються другим кроком після створення), не показуємо. Виняток:
			// desktopOnly — адмін явно позначив «мобільний банер не потрібен»,
			// тоді достатньо desktop-картинки (на мобілці банер не рендериться).
			if (widget.type === "footer_banner") {
				const v = widget.variables || {};
				if (!v.imageDesktop || (!v.imageMobile && v.desktopOnly !== true)) {
					return false;
				}
			}

			if (this.dismissedIds.has(widget.id) || isPersistedDismissed(widget.id)) {
				return false;
			}

			const courseIds = widget.courseIds;

			if (this.placement === "global") {
				return courseIds == null;
			}

			// placement === 'course'
			if (courseIds == null) {
				return false;
			}

			const currentId = this.current ? Number(this.current.id) : null;

			if (currentId === null || Number.isNaN(currentId)) {
				return false;
			}

			const matches = courseIds.some(id => Number(id) === currentId);

			if (!matches) {
				return false;
			}

			if (this.isLesson && widget.showOnLessons === false) {
				return false;
			}

			return true;
		},
		templateFor(type) {
			if (type === "topbar") {
				return "topbar-widget";
			}

			if (type === "footer_banner") {
				return "footer-banner-widget";
			}

			if (type === "footer_promo") {
				return "footer-promo-widget";
			}

			if (type === "generic") {
				return "generic-widget";
			}

			if (type === "custom_html") {
				return "custom-html-widget";
			}

			return null;
		},
		scopeOf(widget) {
			return (widget && widget.variables && widget.variables.dismissScope) || "session";
		},
		dismiss(widgetId, scope) {
			persistDismiss(widgetId, scope);

			const next = new Set(this.dismissedIds);

			next.add(widgetId);

			this.dismissedIds = next;
		},
	},
};
